import {ReducedUser, SmallUser, User, UserIdentity} from "./user";
import {SimpleListItem} from "./admin";

export enum boxType {
    Child = 'child',
    YoungPerson = 'young_person',
    CareLeaver = 'care_leaver',
    Adult = 'adult',
    BirthFamily = 'birth_family',
    FamilyFriend = 'family_friend',
}

export interface ReducedTimeline {
    id: number;
    creator: ReducedUser;
    owner?: ReducedUser;
    first_name: string;
    last_name: string;
    full_name: string;
    preferred_name?: string;
    box_type: boxType;
    box_type_display: string;
    dob?: string;
    reference_number?: string;
    about_me: string;
    photo?: string;
    thumbnail?: string;
    locality?: number;
    cohorts: SimpleListItem[]
    has_cioc_ended: boolean;
    cioc_end_date?: string;
    cioc_warning_7_day_sent_at?: string;
    cioc_warning_30_day_sent_at?: string;
    access: TimelineAccess[];
    created_date: string;
    modified_date: string;
    deletion_requested_date?: string;
    deletion_date?: string;
    deleted_with_owner?: number | null;
    deleted: boolean;
    total_views: number;
    active: boolean;
    last_orphaned_notification_sent?: string;
    last_upload_six_months_or_older_notification_sent?: string
    last_viewed?: string;
    last_uploaded: string;
}

export type TimelineListItem = Omit<ReducedTimeline, 'owner' | 'creator' | 'access'> & {
    creator: SmallUser
    owner?: SmallUser
    owner_in_deletion_queue: boolean
}

export interface MinimalMemory {
    cover_image_thumbnail_url?: string
    cover_image_url?: string
    date?: string
    id: number
    title: string
    type?: 'favourite' | 'hidden'
    creator?: {
        full_name: string
        preferred_name?: string
        username: string
    } | null
}

export interface TimelineMemoriesResponse {
    count: number
    max_year_for_timeline: number
    max_year_in_results: number
    min_year_for_timeline: number
    min_year_in_results: number
    next?: string
    previous?: string
    results: MinimalMemory[],
}

export interface Timeline extends ReducedTimeline {
    access: TimelineAccess[];
}

export type TimelineLabel = Pick<ReducedTimeline,
    'id' | 'first_name' | 'last_name' | 'full_name' | 'preferred_name' | 'box_type' | 'reference_number'>;

export type TimelineCard = TimelineLabel & Pick<ReducedTimeline, 'photo' | 'thumbnail'> & {
    owner: {id: number; role: UserIdentity['role']} | null;
    current_permission: string | null;
};

export interface TimelineAccess {
    id: number;
    user: ReducedUser;
    timeline: number;
    created_date: string;
    modified_date: string;
    deletion_requested_date?: string;
    deleted: boolean;
    permission: string
}

export interface ReducedTimelineAccess {
    id: number;
    timeline: string;
    timeline_id: number;
    timeline_owner?: TimelineOwner;
    permission: string
}

export interface TimelineOwner extends User {
    timeline?: Timeline;
    deletion_date?: string;
    legacy_user_type?: string;
}

export interface TimelinePermission {
    id: number;
    name?: string | null;
    user: ReducedUser;
    timeline: number;
    created_date: string;
    modified_date?: string;
    deletion_requested_date?: string;
    deleted: boolean;
    permission: string
    is_owner: boolean;
    owns_memory_box?: boolean;
}


export interface Memory {
    id: number;
    creator: ReducedUser | null;
    timeline: Timeline;
    cover_image?: string;
    deletion_date?: string;
    created_date: string;
    modified_date?: string;
    deleted: boolean;
    date?: string;
    title: string;
    subtitle?: string;
    description?: string;
    attendees?: string;
    updated_at?: string;
    _old_address?: string;
    address?: string;
    latitude?: string;
    longitude?: string;
    duplication_in_progress: boolean;
    deletion_requested_date?: string;
    tags?: MemoryTags[];
}

export type MemoryWithCoverImage = Omit<Memory, 'cover_image'> & {
    cover_image?: MediaItem;
}

export interface MediaItem {
    id: number;
    is_favourite: boolean;
    user: UserIdentity | null;
    comment_count: number;
    deletion_date: string;
    created_date: string;
    modified_date?: string;
    deleted: boolean;
    cached_file_size: number;
    file_type: string;
    title: string;
    description: string;
    image?: string;
    thumbnail?: string;
    video?: null;
    audio?: null;
    cover_image: boolean;
    document: null;
    document_is_message: boolean;
    hidden: boolean;
    type: string;
    order: number;
    compression_version: number;
    deletion_requested_date?: string;
    hidden_status: string;
    is_processing: boolean;
    requires_heic_processing: boolean;
    requires_rotation_processing: boolean;
    requires_thumbnail_generation: boolean;
    memory: number
}

export interface MemoryTagsWithMemory extends MemoryTags {
    memory: MemoryWithCoverImage
}

export interface MemoryTags {
    id: number;
    name: string;
}

export interface MemoryComment {
    id: number;
    content: string;
    user: UserIdentity;
    memory: number | MemoryWithCoverImage;
    date: string
}

export interface ExportResponse {
    export_started: boolean
    message: string
}

export interface Comment {
    id: number;
    content: string;
    user: ReducedUser;
    media: MediaItem;
    date: string
}

export interface FavMedia {
    id: number;
    memory: {id: number; title: string; timeline: TimelineLabel};
    is_favourite: boolean;
    comment_count: number;
    media: MediaItem;
    created_date: string;
    modified_date?: string;
    deletion_requested_date?: string;
    deleted?: boolean;
    user: number
}

export interface DraftPeriodUpload {
    id: string,
    creator: number,
    timeline: number,
    title: string,
    description: string,
    session_uuid: string,
    items: DraftItem[],
    created_date: string,
    modified_date?: string
}

export interface DraftItem {
    id: string,
    draft: string,
    type: string,
    title: string,
    description?: string,
    added_at: string,
    order: number,
    file?: string,
    message_html?: string,
    message_pdf?: string,
    mime_type: string,
    original_filename: string,
    size_bytes: number,
    created_date: string,
    modified_date?: string
}
